"use client";

import { c as _c } from "react/compiler-runtime";
import { formatPacesetterDate, getPacesetterAuthorizationDetails, getPacesetterFacility, getPacesetterReferralAssignee, getPacesetterSchedulingPriority, getPacesetterStickyNote, getPacesetterStickyNoteFlags } from "@/configuration/groups/pacesetter/routing";
import CoverageCard from "@/src/components/cards/coverage-card/coverage-card";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import ReferralCard from "@/src/components/cards/referral-card/referral-card";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import { Alert, AlertDescription } from "@/src/components/shadcn/ui/alert";
import { ReferralProviderCards } from "@/src/components/tasks/referral-provider-cards";
import { formatDateMmDdYyyy, isMinorDateOfBirth } from "@/src/lib/utils/date-and-phone-utils";
import { Referral } from "@/src/models/documents/referral";
function NotProvided() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span className="text-muted-foreground italic">Not provided</span>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function Field(t0) {
  const $ = _c(7);
  const {
    label,
    value
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="font-medium w-44 shrink-0">{label}:</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = value ? <CopyableContent text={value} /> : <NotProvided />;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex items-baseline gap-1">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function Section(t0) {
  const $ = _c(7);
  const {
    title,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <h3 className="font-semibold text-foreground mb-2">{title}</h3>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children) {
    t2 = <div className="grid grid-cols-3 gap-x-4 gap-y-2">{children}</div>;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div>{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
interface PacesetterReferralInstructionsProps {
  referral: Referral;
  faxRecipientNumber: string | null;
}
export function PacesetterReferralInstructions(t0) {
  const $ = _c(135);
  const {
    referral,
    faxRecipientNumber
  } = t0;
  const section = referral.section;
  let t1;
  if ($[0] !== section.reviewedContent) {
    t1 = section.reviewedContent ?? {};
    $[0] = section.reviewedContent;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const reviewedContent = t1 as Record<string, unknown>;
  const t2 = referral.patient?.dateOfBirth;
  let t3;
  if ($[2] !== t2) {
    t3 = isMinorDateOfBirth(t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const isMinor = t3;
  const t4 = referral.patient?.dateOfBirth ?? null;
  let t5;
  if ($[4] !== faxRecipientNumber || $[5] !== section || $[6] !== t4) {
    t5 = getPacesetterStickyNoteFlags(section, faxRecipientNumber, t4);
    $[4] = faxRecipientNumber;
    $[5] = section;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const {
    authorizationNeeded
  } = t5;
  const t6 = referral.patient?.dateOfBirth ?? null;
  let t7;
  if ($[8] !== faxRecipientNumber || $[9] !== section || $[10] !== t6) {
    t7 = getPacesetterStickyNote(section, faxRecipientNumber, t6);
    $[8] = faxRecipientNumber;
    $[9] = section;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const stickyNote = t7;
  let t8;
  if ($[12] !== faxRecipientNumber) {
    t8 = getPacesetterReferralAssignee(faxRecipientNumber);
    $[12] = faxRecipientNumber;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const assignTo = t8;
  let t9;
  if ($[14] !== faxRecipientNumber) {
    t9 = getPacesetterFacility(faxRecipientNumber);
    $[14] = faxRecipientNumber;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const facility = t9;
  let t10;
  if ($[16] !== reviewedContent) {
    t10 = getPacesetterSchedulingPriority(reviewedContent);
    $[16] = reviewedContent;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const priority = t10;
  let T0;
  let T1;
  let T2;
  let auth;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let taskDetails;
  if ($[18] !== assignTo || $[19] !== authorizationNeeded || $[20] !== facility || $[21] !== isMinor || $[22] !== priority || $[23] !== referral.dateReceived || $[24] !== referral.receivingProvider || $[25] !== section || $[26] !== stickyNote) {
    auth = getPacesetterAuthorizationDetails(section, referral.receivingProvider);
    let t25;
    if ($[46] !== referral.dateReceived) {
      t25 = formatPacesetterDate(referral.dateReceived);
      $[46] = referral.dateReceived;
      $[47] = t25;
    } else {
      t25 = $[47];
    }
    const dateReceived = t25;
    let t26;
    if ($[48] !== referral.dateReceived) {
      t26 = formatDateMmDdYyyy(referral.dateReceived);
      $[48] = referral.dateReceived;
      $[49] = t26;
    } else {
      t26 = $[49];
    }
    const dueDateDisplay = t26;
    taskDetails = dateReceived ? `${dateReceived} Referral` : "Referral";
    t24 = "flex flex-col gap-8 w-full sm:min-w-[800px] max-w-[1200px] mx-auto";
    T2 = EhrInstructionsCard;
    t17 = "flex flex-col gap-6 text-sm";
    if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <span className="font-medium text-center">Pacesetter Referral Instructions:</span>;
      t19 = <p className="text-muted-foreground">Reference for the ModMed task created for this referral. Click any value to copy.</p>;
      $[50] = t18;
      $[51] = t19;
    } else {
      t18 = $[50];
      t19 = $[51];
    }
    if ($[52] !== isMinor) {
      t20 = isMinor && <Alert variant="destructive"><AlertDescription>Minor patient — verify parent/guardian info and DOB. If parent DOB is missing, enter dummy date 01/01/1900. List parent/guardian as the policy holder. Flag the chart if parent info is missing.</AlertDescription></Alert>;
      $[52] = isMinor;
      $[53] = t20;
    } else {
      t20 = $[53];
    }
    if ($[54] !== authorizationNeeded) {
      t21 = authorizationNeeded && <Alert variant="destructive"><AlertDescription>Authorization needed — click the box for Referral/Authorization for office visit and check PCP.</AlertDescription></Alert>;
      $[54] = authorizationNeeded;
      $[55] = t21;
    } else {
      t21 = $[55];
    }
    let t27;
    if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
      t27 = <h3 className="font-semibold text-foreground mb-2">Sticky Note</h3>;
      $[56] = t27;
    } else {
      t27 = $[56];
    }
    if ($[57] !== stickyNote) {
      t22 = <div>{t27}<CopyableContent text={stickyNote} textClassName="whitespace-pre-wrap" /></div>;
      $[57] = stickyNote;
      $[58] = t22;
    } else {
      t22 = $[58];
    }
    let t28;
    if ($[59] !== assignTo) {
      t28 = <Field label="Assign to" value={assignTo} />;
      $[59] = assignTo;
      $[60] = t28;
    } else {
      t28 = $[60];
    }
    let t29;
    if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
      t29 = <Field label="Task Type" value="Schedule" />;
      $[61] = t29;
    } else {
      t29 = $[61];
    }
    let t30;
    if ($[62] !== priority) {
      t30 = <Field label="Priority" value={priority} />;
      $[62] = priority;
      $[63] = t30;
    } else {
      t30 = $[63];
    }
    let t31;
    if ($[64] !== dueDateDisplay) {
      t31 = <Field label="Due date" value={dueDateDisplay} />;
      $[64] = dueDateDisplay;
      $[65] = t31;
    } else {
      t31 = $[65];
    }
    let t32;
    if ($[66] !== taskDetails) {
      t32 = <Field label="Task Details" value={taskDetails} />;
      $[66] = taskDetails;
      $[67] = t32;
    } else {
      t32 = $[67];
    }
    let t33;
    if ($[68] !== facility) {
      t33 = <Field label="Facility" value={facility} />;
      $[68] = facility;
      $[69] = t33;
    } else {
      t33 = $[69];
    }
    if ($[70] !== t28 || $[71] !== t30 || $[72] !== t31 || $[73] !== t32 || $[74] !== t33) {
      t23 = <Section title="Task">{t28}{t29}{t30}{t31}{t32}{t33}</Section>;
      $[70] = t28;
      $[71] = t30;
      $[72] = t31;
      $[73] = t32;
      $[74] = t33;
      $[75] = t23;
    } else {
      t23 = $[75];
    }
    T1 = Section;
    t13 = "Authorization";
    t14 = <Field label="Type" value={auth.type} />;
    t15 = <Field label="Authorization number" value={auth.authorizationNumber} />;
    t16 = <Field label="From" value={formatDateMmDdYyyy(auth.fromDate)} />;
    T0 = Field;
    t11 = "To";
    t12 = formatDateMmDdYyyy(auth.toDate);
    $[18] = assignTo;
    $[19] = authorizationNeeded;
    $[20] = facility;
    $[21] = isMinor;
    $[22] = priority;
    $[23] = referral.dateReceived;
    $[24] = referral.receivingProvider;
    $[25] = section;
    $[26] = stickyNote;
    $[27] = T0;
    $[28] = T1;
    $[29] = T2;
    $[30] = auth;
    $[31] = t11;
    $[32] = t12;
    $[33] = t13;
    $[34] = t14;
    $[35] = t15;
    $[36] = t16;
    $[37] = t17;
    $[38] = t18;
    $[39] = t19;
    $[40] = t20;
    $[41] = t21;
    $[42] = t22;
    $[43] = t23;
    $[44] = t24;
    $[45] = taskDetails;
  } else {
    T0 = $[27];
    T1 = $[28];
    T2 = $[29];
    auth = $[30];
    t11 = $[31];
    t12 = $[32];
    t13 = $[33];
    t14 = $[34];
    t15 = $[35];
    t16 = $[36];
    t17 = $[37];
    t18 = $[38];
    t19 = $[39];
    t20 = $[40];
    t21 = $[41];
    t22 = $[42];
    t23 = $[43];
    t24 = $[44];
    taskDetails = $[45];
  }
  let t25;
  if ($[76] !== T0 || $[77] !== t11 || $[78] !== t12) {
    t25 = <T0 label={t11} value={t12} />;
    $[76] = T0;
    $[77] = t11;
    $[78] = t12;
    $[79] = t25;
  } else {
    t25 = $[79];
  }
  const t26 = auth.numberOfVisits != null ? String(auth.numberOfVisits) : null;
  let t27;
  if ($[80] !== t26) {
    t27 = <Field label="Number of visits" value={t26} />;
    $[80] = t26;
    $[81] = t27;
  } else {
    t27 = $[81];
  }
  let t28;
  if ($[82] !== auth.description) {
    t28 = <Field label="Description" value={auth.description} />;
    $[82] = auth.description;
    $[83] = t28;
  } else {
    t28 = $[83];
  }
  let t29;
  if ($[84] !== auth.assignedProviderText) {
    t29 = <Field label="Assigned provider" value={auth.assignedProviderText} />;
    $[84] = auth.assignedProviderText;
    $[85] = t29;
  } else {
    t29 = $[85];
  }
  let t30;
  if ($[86] !== auth.referringProviderText) {
    t30 = <Field label="Referring provider" value={auth.referringProviderText} />;
    $[86] = auth.referringProviderText;
    $[87] = t30;
  } else {
    t30 = $[87];
  }
  let t31;
  if ($[88] !== auth.referringProviderNpi) {
    t31 = <Field label="Referring provider NPI" value={auth.referringProviderNpi} />;
    $[88] = auth.referringProviderNpi;
    $[89] = t31;
  } else {
    t31 = $[89];
  }
  let t32;
  if ($[90] !== T1 || $[91] !== t13 || $[92] !== t14 || $[93] !== t15 || $[94] !== t16 || $[95] !== t25 || $[96] !== t27 || $[97] !== t28 || $[98] !== t29 || $[99] !== t30 || $[100] !== t31) {
    t32 = <T1 title={t13}>{t14}{t15}{t16}{t25}{t27}{t28}{t29}{t30}{t31}</T1>;
    $[90] = T1;
    $[91] = t13;
    $[92] = t14;
    $[93] = t15;
    $[94] = t16;
    $[95] = t25;
    $[96] = t27;
    $[97] = t28;
    $[98] = t29;
    $[99] = t30;
    $[100] = t31;
    $[101] = t32;
  } else {
    t32 = $[101];
  }
  let t33;
  if ($[102] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <Field label="Category" value="Referral" />;
    $[102] = t33;
  } else {
    t33 = $[102];
  }
  let t34;
  if ($[103] !== taskDetails) {
    t34 = <Section title="Document">{t33}<Field label="Label" value={taskDetails} /></Section>;
    $[103] = taskDetails;
    $[104] = t34;
  } else {
    t34 = $[104];
  }
  let t35;
  if ($[105] !== t17 || $[106] !== t18 || $[107] !== t19 || $[108] !== t20 || $[109] !== t21 || $[110] !== t22 || $[111] !== t23 || $[112] !== t32 || $[113] !== t34) {
    t35 = <div className={t17}>{t18}{t19}{t20}{t21}{t22}{t23}{t32}{t34}</div>;
    $[105] = t17;
    $[106] = t18;
    $[107] = t19;
    $[108] = t20;
    $[109] = t21;
    $[110] = t22;
    $[111] = t23;
    $[112] = t32;
    $[113] = t34;
    $[114] = t35;
  } else {
    t35 = $[114];
  }
  let t36;
  if ($[115] !== T2 || $[116] !== t35) {
    t36 = <T2>{t35}</T2>;
    $[115] = T2;
    $[116] = t35;
    $[117] = t36;
  } else {
    t36 = $[117];
  }
  let t37;
  if ($[118] !== referral.patient) {
    t37 = <PatientCard patient={referral.patient} />;
    $[118] = referral.patient;
    $[119] = t37;
  } else {
    t37 = $[119];
  }
  let t38;
  let t39;
  if ($[120] !== referral) {
    t38 = <ReferralProviderCards referral={referral} />;
    t39 = <ReferralCard referral={referral} isEditable={true} />;
    $[120] = referral;
    $[121] = t38;
    $[122] = t39;
  } else {
    t38 = $[121];
    t39 = $[122];
  }
  let t40;
  if ($[123] !== referral.primaryCoverage) {
    t40 = referral.primaryCoverage && <CoverageCard coverage={referral.primaryCoverage} type="Primary" isEditable={true} />;
    $[123] = referral.primaryCoverage;
    $[124] = t40;
  } else {
    t40 = $[124];
  }
  let t41;
  if ($[125] !== referral.secondaryCoverage) {
    t41 = referral.secondaryCoverage && <CoverageCard coverage={referral.secondaryCoverage} type="Secondary" isEditable={true} />;
    $[125] = referral.secondaryCoverage;
    $[126] = t41;
  } else {
    t41 = $[126];
  }
  let t42;
  if ($[127] !== t24 || $[128] !== t36 || $[129] !== t37 || $[130] !== t38 || $[131] !== t39 || $[132] !== t40 || $[133] !== t41) {
    t42 = <div className={t24}>{t36}{t37}{t38}{t39}{t40}{t41}</div>;
    $[127] = t24;
    $[128] = t36;
    $[129] = t37;
    $[130] = t38;
    $[131] = t39;
    $[132] = t40;
    $[133] = t41;
    $[134] = t42;
  } else {
    t42 = $[134];
  }
  return t42;
}